import { useState } from 'react';
import { cn } from '@/lib/cn';

/**
 * Celda numérica que se vuelve input al hacer click. Guarda con Enter o al salir; Escape descarta.
 *
 * Vive acá y no dentro de una pantalla porque la usan DOS: la tabla de Configuración y el
 * Semáforo de Inventario. Charly pidió poder corregir el tiempo de producción y el valor
 * unitario **desde el semáforo**, que es donde se ve que faltan (2026-08-04) — mandar a alguien
 * a otra pantalla para arreglar lo que está mirando es fricción sin motivo.
 *
 * No guarda si el valor no cambió: evita un PATCH por cada click accidental.
 */
export function EditableCell({
  valor,
  formato,
  sufijo,
  sinCargar,
  onGuardar,
  className,
}: {
  valor: number;
  formato: (v: number) => string;
  sufijo?: string;
  /** true = el valor es 0 porque nadie lo cargó, no porque valga cero. Se dice, no se muestra "$0". */
  sinCargar?: boolean;
  onGuardar: (v: number) => void;
  className?: string;
}) {
  const [editando, setEditando] = useState(false);
  const [texto, setTexto] = useState(String(valor));

  const confirmar = () => {
    setEditando(false);
    const num = Number(texto);
    if (!Number.isFinite(num) || num < 0 || num === valor) {
      setTexto(String(valor));
      return;
    }
    onGuardar(num);
  };

  if (editando) {
    return (
      <input
        autoFocus
        type="number"
        min={0}
        value={texto}
        onChange={(e) => setTexto(e.target.value)}
        onBlur={confirmar}
        onKeyDown={(e) => {
          if (e.key === 'Enter') confirmar();
          if (e.key === 'Escape') {
            setTexto(String(valor));
            setEditando(false);
          }
        }}
        className="h-8 w-28 rounded-lg border border-primary bg-panel px-2 text-right text-sm text-ink-strong focus:outline-none"
      />
    );
  }

  return (
    <button
      onClick={() => {
        setTexto(String(valor));
        setEditando(true);
      }}
      className={cn('rounded px-1.5 py-0.5 text-sm hover:bg-hover-fuerte', className)}
      title="Click para editar"
    >
      <span className={cn('font-semibold', sinCargar ? 'text-warning-text' : 'text-ink-strong')}>
        {sinCargar ? 'sin cargar' : formato(valor)}
      </span>
      {sufijo && !sinCargar && <span className="ml-1 text-2xs text-ink-faint">{sufijo}</span>}
    </button>
  );
}

/**
 * La misma celda, pero para un valor que **puede estar en automático**.
 *
 * Es un componente aparte y no una prop de `EditableCell` porque la semántica es otra: acá
 * VACIAR el campo es una acción con significado —"volvé a calcularlo vos"— y no un error de
 * tipeo. Mezclarlas obligaría a que la de siempre acepte `null` y a que sus tres llamadores
 * existentes se hagan cargo de un caso que en su columna no existe.
 *
 * ⚠️ `null` y `0` NO son lo mismo, y por eso el input vacío tiene que poder guardarse: `0` es
 * "sin colchón" o "reorden en cero", dos decisiones legítimas. Es la misma regla que rige la
 * planilla de carga de este proyecto y la que en BET 2 dejó 90 productos publicados a $0
 * cuando se rellenó un hueco con un cero.
 */
export function CeldaAuto({
  valor,
  formato,
  /** Lo que el motor calcula hoy. Se muestra en gris cuando está en automático. */
  auto,
  onGuardar,
  className,
}: {
  valor: number | null;
  formato: (v: number) => string;
  auto?: number | null;
  onGuardar: (v: number | null) => void;
  className?: string;
}) {
  const [editando, setEditando] = useState(false);
  const [texto, setTexto] = useState(valor == null ? '' : String(valor));

  const confirmar = () => {
    setEditando(false);
    const limpio = texto.trim();
    if (limpio === '') {
      if (valor != null) onGuardar(null); // vaciar = volver a automático
      return;
    }
    const num = Number(limpio);
    if (!Number.isFinite(num) || num < 0 || num === valor) {
      setTexto(valor == null ? '' : String(valor));
      return;
    }
    onGuardar(num);
  };

  if (editando) {
    return (
      <input
        autoFocus
        type="number"
        min={0}
        value={texto}
        onChange={(e) => setTexto(e.target.value)}
        onBlur={confirmar}
        onKeyDown={(e) => {
          if (e.key === 'Enter') confirmar();
          if (e.key === 'Escape') {
            setTexto(valor == null ? '' : String(valor));
            setEditando(false);
          }
        }}
        placeholder="auto"
        className="h-8 w-28 rounded-lg border border-primary bg-panel px-2 text-right text-sm text-ink-strong focus:outline-none"
      />
    );
  }

  return (
    <button
      onClick={() => {
        setTexto(valor == null ? '' : String(valor));
        setEditando(true);
      }}
      className={cn('rounded px-1.5 py-0.5 text-sm hover:bg-hover-fuerte', className)}
      title={valor == null ? 'Lo calcula el motor. Click para fijarlo a mano' : 'Click para editar; vaciá el campo para volver a automático'}
    >
      {valor == null ? (
        <>
          <span className="text-sm text-ink-faint">auto</span>
          {/* Se muestra lo que el motor calcula HOY: sin eso, "auto" no le dice a nadie qué
              número está usando el sistema, y decidir si conviene fijarlo sería a ciegas. */}
          {/* `auto === 0` SÍ se muestra: "auto (0%)" dice que el motor decidió que este
              producto no necesita colchón, que es información. Esconderlo dejaría al usuario
              sin saber si es cero o si no se pudo calcular. */}
          {auto != null && <span className="ml-1.5 text-2xs text-ink-faint">({formato(auto)})</span>}
        </>
      ) : (
        <span className="font-semibold text-primary-text">{formato(valor)}</span>
      )}
    </button>
  );
}
